'use client';

import Divider, { type DividerProps } from '@mui/material/Divider';
import Link, { type LinkProps } from '@mui/material/Link';
import Stack, { type StackProps } from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography, { type TypographyProps } from '@mui/material/Typography';

import PriceDrop from '@/assets/icons/price-drop.svg?react';

export const MiniCarTileLinkWrapper = styled(Link)<LinkProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '1.125rem',
  color: theme.palette.text.primary,
  display: 'flex',
  overflow: 'hidden',
  textDecoration: 'none',

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },

  '&[data-orientation="horizontal"]': {
    alignItems: 'center',
    border: `1px solid ${theme.palette.divider}`,
    gap: '1rem',
    padding: '0.5rem',

    '&:hover': {
      backgroundColor: theme.palette.secondary.faint,
    },

    '&:focus-visible': {
      outlineOffset: -1,
    },
  },

  '&[data-orientation="vertical"]': {
    gap: 0,
    padding: '0 0.25rem 0.5rem',
    alignItems: 'start',
    flexDirection: 'column',
  },
}));

export const ImageWrapper = styled('div')(() => ({
  borderRadius: 'calc(1.125rem - 0.25rem)',
  flex: '0 0 auto',
  height: '5.5rem',
  overflow: 'hidden',
  position: 'relative',
  aspectRatio: '4 / 3',

  img: {
    display: 'block',
    height: '100%',
    objectFit: 'cover',
    width: '100%',

    '[data-unavailable="true"] &': {
      opacity: 0.5,
    },
  },

  '.kmx-car-tile__favorite-button': {
    position: 'absolute',
    right: 0,
    top: 0,
  },

  '[data-orientation="vertical"][data-variant="mini"] & ': {
    height: 'auto',
    marginLeft: '-0.25rem',
    width: 'calc(100% + 0.5rem)',

    '.kmx-car-tile__favorite-button': {
      right: '0.25rem',
      top: '0.25rem',
    },
  },
}));

export const ContentWrapper = styled(Stack)<StackProps>(({ theme }) => ({
  overflow: 'hidden',

  '[data-unavailable="true"] & .MuiTypography-root': {
    color: theme.palette.text.disabled,
  },
}));

export const YearMakeModel = styled(Typography)<TypographyProps>(
  ({ theme }) => ({
    'a:visited &': {
      color: theme.palette.flagged.dark,
    },
  }),
);

export const PriceMilesInfo = styled(Typography)<TypographyProps>(() => ({
  fontWeight: 700,
  verticalAlign: 'middle',
}));

export const PriceMilesDivider = styled(Divider)<DividerProps>(() => ({
  display: 'inline',
  margin: '0 0.5rem',
}));

export const PriceDropIcon = styled(PriceDrop)(({ theme }) => ({
  display: 'inline-block',

  '[data-unavailable="true"] & path': {
    fill: theme.palette.action.disabledBackground,
  },
}));

export const OriginalPrice = styled(Typography)<TypographyProps>(() => ({
  textDecoration: 'line-through',
}));
